'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

type View = 'landing' | 'assessment' | 'results';

interface Question {
  id: string;
  text: string;
  article: string;
  weight: number;
}

interface Section {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

const sections: Section[] = [
  {
    id: 'ict-risk-management',
    title: 'ICT Risk Management',
    description: 'Framework for managing information and communication technology risks (Articles 5-16)',
    questions: [
      { id: 'rm1', text: 'Do you have a comprehensive ICT risk management framework in place?', article: 'Art. 6', weight: 10 },
      { id: 'rm2', text: 'Is there a dedicated ICT risk management function independent from business units?', article: 'Art. 6(4)', weight: 8 },
      { id: 'rm3', text: 'Do you maintain an up-to-date inventory of all ICT assets?', article: 'Art. 8', weight: 9 },
      { id: 'rm4', text: 'Are ICT assets classified according to criticality and sensitivity?', article: 'Art. 8', weight: 8 },
      { id: 'rm5', text: 'Is there a documented ICT risk assessment methodology?', article: 'Art. 9', weight: 9 },
      { id: 'rm6', text: 'Are ICT risk assessments performed at least annually?', article: 'Art. 9', weight: 8 },
      { id: 'rm7', text: 'Is the management body actively involved in ICT risk decisions?', article: 'Art. 5(2)', weight: 9 },
      { id: 'rm8', text: 'Are ICT risks integrated into the overall risk management framework?', article: 'Art. 6(8)', weight: 8 },
    ],
  },
  {
    id: 'ict-incident-management',
    title: 'ICT Incident Management',
    description: 'Detection, classification, and reporting of ICT-related incidents (Articles 17-23)',
    questions: [
      { id: 'im1', text: 'Do you have an ICT-related incident management process?', article: 'Art. 17', weight: 10 },
      { id: 'im2', text: 'Are incidents classified according to a defined taxonomy (major/non-major)?', article: 'Art. 18', weight: 9 },
      { id: 'im3', text: 'Is there a process for detecting anomalous activities?', article: 'Art. 17', weight: 8 },
      { id: 'im4', text: 'Can you report major ICT incidents to competent authorities within required timeframes?', article: 'Art. 19', weight: 10 },
      { id: 'im5', text: 'Do you maintain records of all ICT incidents?', article: 'Art. 17(3)', weight: 8 },
      { id: 'im6', text: 'Is there a process for root cause analysis of incidents?', article: 'Art. 17(3)', weight: 8 },
      { id: 'im7', text: 'Are lessons learned from incidents incorporated into risk management?', article: 'Art. 17(3)', weight: 7 },
      { id: 'im8', text: 'Do you have procedures for customer notification of significant incidents?', article: 'Art. 20', weight: 8 },
    ],
  },
  {
    id: 'digital-resilience-testing',
    title: 'Digital Operational Resilience Testing',
    description: 'Testing program for ICT systems and controls (Articles 24-27)',
    questions: [
      { id: 'dt1', text: 'Do you have a digital operational resilience testing program?', article: 'Art. 24', weight: 10 },
      { id: 'dt2', text: 'Does the testing program cover all critical ICT systems?', article: 'Art. 24', weight: 9 },
      { id: 'dt3', text: 'Are vulnerability assessments performed regularly?', article: 'Art. 25', weight: 9 },
      { id: 'dt4', text: 'Are penetration tests conducted at least annually?', article: 'Art. 25', weight: 9 },
      { id: 'dt5', text: 'Do you perform threat-led penetration testing (TLPT) where required?', article: 'Art. 26', weight: 10 },
      { id: 'dt6', text: 'Are testers independent from the tested systems?', article: 'Art. 24(6)', weight: 7 },
      { id: 'dt7', text: 'Is there a process to remediate findings from resilience tests?', article: 'Art. 24(5)', weight: 8 },
      { id: 'dt8', text: 'Are testing results reported to the management body?', article: 'Art. 24(5)', weight: 7 },
    ],
  },
  {
    id: 'third-party-risk',
    title: 'ICT Third-Party Risk Management',
    description: 'Managing risks from ICT third-party service providers (Articles 28-44)',
    questions: [
      { id: 'tp1', text: 'Do you maintain a register of all ICT third-party arrangements?', article: 'Art. 28(3)', weight: 10 },
      { id: 'tp2', text: 'Are third-party providers classified by criticality?', article: 'Art. 28', weight: 9 },
      { id: 'tp3', text: 'Do contracts include required DORA provisions?', article: 'Art. 30', weight: 10 },
      { id: 'tp4', text: 'Is there due diligence process before engaging ICT providers?', article: 'Art. 28(4)', weight: 9 },
      { id: 'tp5', text: 'Are there clear exit strategies for critical providers?', article: 'Art. 28(8)', weight: 9 },
      { id: 'tp6', text: 'Do you monitor provider performance against SLAs?', article: 'Art. 30', weight: 8 },
      { id: 'tp7', text: 'Is there oversight of subcontracting by providers?', article: 'Art. 29', weight: 8 },
      { id: 'tp8', text: 'Do you assess concentration risk from third-party dependencies?', article: 'Art. 28(4)', weight: 9 },
    ],
  },
  {
    id: 'information-sharing',
    title: 'Information Sharing',
    description: 'Arrangements for sharing cyber threat intelligence (Article 45)',
    questions: [
      { id: 'is1', text: 'Do you participate in information sharing arrangements on cyber threats?', article: 'Art. 45', weight: 7 },
      { id: 'is2', text: 'Are there procedures for receiving and processing threat intelligence?', article: 'Art. 45', weight: 7 },
      { id: 'is3', text: 'Is threat intelligence integrated into risk management?', article: 'Art. 45', weight: 7 },
      { id: 'is4', text: 'Are there safeguards for confidentiality of shared information?', article: 'Art. 45(2)', weight: 8 },
    ],
  },
  {
    id: 'governance',
    title: 'Governance & Oversight',
    description: 'Management body responsibilities and organizational requirements',
    questions: [
      { id: 'go1', text: 'Does the management body define and oversee the ICT risk strategy?', article: 'Art. 5', weight: 10 },
      { id: 'go2', text: 'Are there clear roles and responsibilities for ICT risk management?', article: 'Art. 5(2)', weight: 9 },
      { id: 'go3', text: 'Does the management body receive regular ICT risk reports?', article: 'Art. 5(2)', weight: 8 },
      { id: 'go4', text: 'Do management body members receive ICT risk training?', article: 'Art. 5(4)', weight: 8 },
      { id: 'go5', text: 'Is there adequate budget allocated for ICT security?', article: 'Art. 5(2)', weight: 8 },
      { id: 'go6', text: 'Are ICT policies approved by the management body?', article: 'Art. 5(2)', weight: 8 },
    ],
  },
  {
    id: 'business-continuity',
    title: 'Business Continuity',
    description: 'ICT business continuity and disaster recovery capabilities (Articles 11-12)',
    questions: [
      { id: 'bc1', text: 'Is there an ICT business continuity policy?', article: 'Art. 11', weight: 10 },
      { id: 'bc2', text: 'Are business impact analyses performed for critical functions?', article: 'Art. 11', weight: 9 },
      { id: 'bc3', text: 'Are there documented ICT disaster recovery plans?', article: 'Art. 11', weight: 10 },
      { id: 'bc4', text: 'Are recovery time objectives (RTO) defined for critical systems?', article: 'Art. 11', weight: 9 },
      { id: 'bc5', text: 'Are recovery point objectives (RPO) defined for critical data?', article: 'Art. 11', weight: 9 },
      { id: 'bc6', text: 'Are backup and restoration procedures tested regularly?', article: 'Art. 12', weight: 9 },
      { id: 'bc7', text: 'Are redundant ICT capabilities maintained for critical systems?', article: 'Art. 11', weight: 8 },
      { id: 'bc8', text: 'Is crisis communication planned and tested?', article: 'Art. 11', weight: 7 },
    ],
  },
];

const totalWeight = sections.reduce(
  (sum, section) => sum + section.questions.reduce((qSum, q) => qSum + q.weight, 0),
  0
);

export default function DORACompliance() {
  const [view, setView] = useState<View>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Record<string, 'yes' | 'no' | 'partial' | 'na'>>({});
  const [entityType, setEntityType] = useState<string>('');
  const [saved, setSaved] = useState(false);

  const {
    enabled: commandCenterEnabled,
    saving,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'dora-compliance',
    toolCategory: 'compliance',
  });

  const handleSaveToCommandCenter = async () => {
    const { score, gaps } = calculateScore();
    const sectionScores: Record<string, number> = {};
    sections.forEach((section) => {
      sectionScores[section.id] = getSectionScore(section.id);
    });

    const result = await saveResult({
      name: 'DORA Compliance Assessment',
      score_overall: score,
      scores: sectionScores,
      answers,
      gaps: gaps.map((g, i) => ({
        id: `gap-${i}`,
        title: g.question,
        severity: g.status === 'Not Implemented' ? 'high' : 'medium',
        description: `Article: ${g.article}`,
      })),
      recommendations: score < 90 ? [
        { id: 'rec-1', title: 'Address ICT risk management framework gaps', priority: 'high' },
        { id: 'rec-2', title: 'Implement incident management and reporting', priority: 'high' },
        { id: 'rec-3', title: 'Establish digital resilience testing program', priority: 'high' },
        { id: 'rec-4', title: 'Review ICT third-party contracts', priority: 'medium' },
        { id: 'rec-5', title: 'Develop exit strategies for critical providers', priority: 'medium' },
      ] : [],
      metadata: { entityType },
    });

    if (result) setSaved(true);
  };

  const handleAnswer = (questionId: string, value: 'yes' | 'no' | 'partial' | 'na') => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateScore = () => {
    let earned = 0;
    let possible = 0;
    const gaps: { article: string; question: string; status: string }[] = [];

    sections.forEach((section) => {
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === 'na') return;

        possible += q.weight;
        if (answer === 'yes') {
          earned += q.weight;
        } else if (answer === 'partial') {
          earned += q.weight * 0.5;
          gaps.push({ article: q.article, question: q.text, status: 'Partial' });
        } else if (answer === 'no') {
          gaps.push({ article: q.article, question: q.text, status: 'Not Implemented' });
        }
      });
    });

    return {
      score: possible > 0 ? Math.round((earned / possible) * 100) : 0,
      gaps: gaps.sort((a, b) => a.article.localeCompare(b.article)),
    };
  };

  const getSectionScore = (sectionId: string) => {
    const section = sections.find((s) => s.id === sectionId);
    if (!section) return 0;

    let earned = 0;
    let possible = 0;

    section.questions.forEach((q) => {
      const answer = answers[q.id];
      if (answer === 'na') return;
      possible += q.weight;
      if (answer === 'yes') earned += q.weight;
      else if (answer === 'partial') earned += q.weight * 0.5;
    });

    return possible > 0 ? Math.round((earned / possible) * 100) : 0;
  };

  const getComplianceLevel = (score: number) => {
    if (score >= 90) return { level: 'Compliant', color: 'text-green-400', bg: 'bg-green-500/20' };
    if (score >= 70) return { level: 'Substantially Compliant', color: 'text-yellow-400', bg: 'bg-yellow-500/20' };
    if (score >= 50) return { level: 'Partially Compliant', color: 'text-accent-400', bg: 'bg-accent-500/20' };
    return { level: 'Not Compliant', color: 'text-red-400', bg: 'bg-red-500/20' };
  };

  const exportToMarkdown = () => {
    const { score, gaps } = calculateScore();
    const compliance = getComplianceLevel(score);

    let md = `# DORA Compliance Assessment Report\n\n`;
    md += `**Generated:** ${new Date().toLocaleDateString()}\n\n`;
    md += `**Entity Type:** ${entityType || 'Not specified'}\n\n`;
    md += `## Overall Compliance Score\n\n`;
    md += `**Score:** ${score}%\n`;
    md += `**Status:** ${compliance.level}\n\n`;

    md += `## Pillar Scores\n\n`;
    md += `| DORA Pillar | Score |\n`;
    md += `|-------------|-------|\n`;
    sections.forEach((section) => {
      md += `| ${section.title} | ${getSectionScore(section.id)}% |\n`;
    });

    if (gaps.length > 0) {
      md += `\n## Compliance Gaps\n\n`;
      md += `| Article | Issue | Status |\n`;
      md += `|---------|-------|--------|\n`;
      gaps.forEach((gap) => {
        md += `| ${gap.article} | ${gap.question} | ${gap.status} |\n`;
      });
    }

    md += `\n## DORA Timeline\n\n`;
    md += `| Date | Milestone |\n`;
    md += `|------|----------|\n`;
    md += `| January 17, 2025 | DORA becomes applicable |\n`;
    md += `| Ongoing | Regulatory Technical Standards (RTS) adoption |\n`;
    md += `| Post-2025 | Critical ICT third-party provider oversight framework |\n`;

    md += `\n## Recommended Actions\n\n`;
    if (score < 90) {
      md += `1. Address high-priority gaps in ICT risk management framework\n`;
      md += `2. Implement comprehensive incident management and reporting\n`;
      md += `3. Establish digital operational resilience testing program\n`;
      md += `4. Review and update ICT third-party contracts\n`;
      md += `5. Develop exit strategies for critical providers\n`;
      md += `6. Ensure management body training on ICT risks\n`;
    } else {
      md += `1. Maintain current controls and documentation\n`;
      md += `2. Monitor regulatory guidance and RTS updates\n`;
      md += `3. Continue regular testing and assessment cycle\n`;
      md += `4. Engage with supervisory authorities as required\n`;
    }

    md += `\n---\n*Assessment conducted using The Art of CTO DORA Compliance Assessment*\n`;

    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `dora-compliance-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (view === 'landing') {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Link href="/tools" className="text-foreground-tertiary hover:text-accent mb-8 inline-block">
            ← Back to Tools
          </Link>

          <div className="text-center mb-12">
            <span className="text-accent text-sm font-medium tracking-wider uppercase">
              EU Financial Services
            </span>
            <h1 className="text-4xl font-bold text-foreground mt-2 mb-4">
              DORA Compliance Assessment
            </h1>
            <p className="text-foreground-secondary text-lg max-w-2xl mx-auto">
              Assess your organization&apos;s readiness for the Digital Operational Resilience Act (DORA).
              Identify gaps across ICT risk management, incident handling, testing, and third-party management.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">What is DORA?</h3>
              <p className="text-foreground-secondary text-sm">
                The Digital Operational Resilience Act (Regulation EU 2022/2554) establishes
                uniform requirements for ICT risk management and resilience in the EU
                financial sector. It applies from January 17, 2025.
              </p>
            </div>
            <div className="bg-surface border border-border-muted rounded-lg p-6">
              <h3 className="text-foreground font-semibold mb-3">Who Must Comply?</h3>
              <p className="text-foreground-secondary text-sm">
                Banks, insurance companies, investment firms, payment institutions,
                crypto-asset service providers, and other financial entities.
                Also applies to critical ICT third-party service providers.
              </p>
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">Select Your Entity Type</h3>
            <select
              value={entityType}
              onChange={(e) => setEntityType(e.target.value)}
              className="w-full bg-background border border-border rounded-lg px-4 py-2 text-foreground"
            >
              <option value="">Select entity type...</option>
              <option value="credit-institution">Credit Institution (Bank)</option>
              <option value="investment-firm">Investment Firm</option>
              <option value="insurance">Insurance/Reinsurance Undertaking</option>
              <option value="payment-institution">Payment Institution</option>
              <option value="e-money">Electronic Money Institution</option>
              <option value="crypto-asset">Crypto-asset Service Provider</option>
              <option value="fund-manager">Fund Manager (AIFM/UCITS)</option>
              <option value="trading-venue">Trading Venue</option>
              <option value="ict-provider">Critical ICT Third-Party Provider</option>
              <option value="other">Other Financial Entity</option>
            </select>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-3">Five Pillars of DORA</h3>
            <div className="space-y-3 text-sm text-foreground-secondary">
              <div className="flex gap-3">
                <span className="text-accent font-bold">1.</span>
                <div>
                  <span className="text-foreground font-medium">ICT Risk Management</span>
                  <p>Comprehensive framework for identifying, protecting, detecting, responding to ICT risks</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold">2.</span>
                <div>
                  <span className="text-foreground font-medium">ICT Incident Management</span>
                  <p>Detection, classification, and reporting of ICT-related incidents</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold">3.</span>
                <div>
                  <span className="text-foreground font-medium">Digital Resilience Testing</span>
                  <p>Testing of ICT systems including threat-led penetration testing (TLPT)</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold">4.</span>
                <div>
                  <span className="text-foreground font-medium">Third-Party Risk Management</span>
                  <p>Oversight of ICT service providers and contractual requirements</p>
                </div>
              </div>
              <div className="flex gap-3">
                <span className="text-accent font-bold">5.</span>
                <div>
                  <span className="text-foreground font-medium">Information Sharing</span>
                  <p>Arrangements for sharing cyber threat intelligence</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-surface border border-border rounded-lg p-6 mb-8">
            <h3 className="text-accent font-semibold mb-2">Key Deadline</h3>
            <p className="text-foreground">
              <span className="font-bold">January 17, 2025:</span>{' '}
              <span className="text-foreground-secondary">
                DORA becomes applicable. All in-scope entities must be compliant.
              </span>
            </p>
          </div>

          <div className="text-center">
            <button
              onClick={() => setView('assessment')}
              className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-8 py-3 rounded-lg transition-colors"
            >
              Start DORA Assessment
            </button>
            <p className="text-foreground-tertiary text-sm mt-4">
              Takes approximately 15-20 minutes to complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (view === 'assessment') {
    const section = sections[currentSection];
    const progress = ((currentSection + 1) / sections.length) * 100;

    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => setView('landing')}
              className="text-foreground-tertiary hover:text-accent"
            >
              ← Back
            </button>
            <span className="text-foreground-tertiary text-sm">
              Pillar {currentSection + 1} of {sections.length}
            </span>
          </div>

          <div className="mb-8">
            <div className="flex justify-between text-sm text-foreground-tertiary mb-2">
              <span>{section.title}</span>
              <span>{Math.round(progress)}%</span>
            </div>
            <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
              <div
                className="h-full bg-accent transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-6">
            <h2 className="text-xl font-semibold text-foreground mb-2">{section.title}</h2>
            <p className="text-foreground-tertiary">{section.description}</p>
          </div>

          <div className="space-y-4">
            {section.questions.map((question) => (
              <div
                key={question.id}
                className="bg-surface border border-border-muted rounded-lg p-4"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="text-accent text-xs font-medium">
                      {question.article}
                    </span>
                    <p className="text-foreground mt-1">{question.text}</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {(['yes', 'partial', 'no', 'na'] as const).map((value) => (
                    <button
                      key={value}
                      onClick={() => handleAnswer(question.id, value)}
                      className={`px-4 py-2 rounded text-sm font-medium transition-colors ${
                        answers[question.id] === value
                          ? value === 'yes'
                            ? 'bg-green-500/20 text-green-400 border border-green-500/50'
                            : value === 'partial'
                            ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                            : value === 'no'
                            ? 'bg-red-500/20 text-red-400 border border-red-500/50'
                            : 'bg-gray-500/20 text-gray-400 border border-gray-500/50'
                          : 'bg-cream/5 text-foreground-tertiary border border-border-muted hover:border-border/30'
                      }`}
                    >
                      {value === 'na' ? 'N/A' : value.charAt(0).toUpperCase() + value.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-between mt-8">
            <button
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
              className="px-6 py-2 rounded-lg border border-border text-foreground disabled:opacity-50 disabled:cursor-not-allowed hover:border-border/40 transition-colors"
            >
              Previous
            </button>
            {currentSection < sections.length - 1 ? (
              <button
                onClick={() => setCurrentSection(currentSection + 1)}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                Next Pillar
              </button>
            ) : (
              <button
                onClick={() => setView('results')}
                className="bg-accent hover:bg-accent-hover text-accent-foreground font-semibold px-6 py-2 rounded-lg transition-colors"
              >
                View Results
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { score, gaps } = calculateScore();
  const compliance = getComplianceLevel(score);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => setView('assessment')}
            className="text-foreground-tertiary hover:text-accent"
          >
            ← Back to Assessment
          </button>
          <button
            onClick={exportToMarkdown}
            className="bg-cream/10 hover:bg-cream/20 text-foreground px-4 py-2 rounded-lg transition-colors"
          >
            Export Report
          </button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-foreground mb-4">
            DORA Compliance Results
          </h1>
          <div className={`inline-block px-6 py-3 rounded-lg ${compliance.bg}`}>
            <span className={`text-4xl font-bold ${compliance.color}`}>{score}%</span>
            <p className={`${compliance.color} font-medium mt-1`}>{compliance.level}</p>
          </div>
          {entityType && (
            <p className="text-foreground-tertiary text-sm mt-4">Entity Type: {entityType}</p>
          )}
        </div>

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Pillar Scores</h2>
          <div className="space-y-3">
            {sections.map((section) => {
              const sectionScore = getSectionScore(section.id);
              const sectionCompliance = getComplianceLevel(sectionScore);
              return (
                <div key={section.id}>
                  <div className="flex justify-between text-sm mb-1">
                    <span className="text-foreground-secondary">{section.title}</span>
                    <span className={sectionCompliance.color}>{sectionScore}%</span>
                  </div>
                  <div className="h-2 bg-cream/10 rounded-full overflow-hidden">
                    <div
                      className={`h-full transition-all ${
                        sectionScore >= 90
                          ? 'bg-green-500'
                          : sectionScore >= 70
                          ? 'bg-yellow-500'
                          : sectionScore >= 50
                          ? 'bg-accent'
                          : 'bg-red-500'
                      }`}
                      style={{ width: `${sectionScore}%` }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {gaps.length > 0 && (
          <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-foreground mb-4">
              Compliance Gaps ({gaps.length})
            </h2>
            <div className="space-y-3">
              {gaps.map((gap, index) => (
                <div
                  key={index}
                  className="border border-border-muted rounded-lg p-3"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <span className="text-accent text-xs font-medium">
                        {gap.article}
                      </span>
                      <p className="text-foreground text-sm mt-1">{gap.question}</p>
                    </div>
                    <span
                      className={`text-xs font-medium px-2 py-1 rounded ${
                        gap.status === 'Partial'
                          ? 'bg-yellow-500/20 text-yellow-400'
                          : 'bg-red-500/20 text-red-400'
                      }`}
                    >
                      {gap.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-surface border border-border-muted rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Key Requirements</h2>
          <div className="text-foreground-secondary text-sm space-y-3">
            <div>
              <h4 className="text-foreground font-medium mb-1">Management Body Responsibilities</h4>
              <p>The management body must define, approve, and oversee the ICT risk management framework,
                 allocate adequate resources, and receive regular training.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">Incident Reporting</h4>
              <p>Major ICT-related incidents must be reported to competent authorities within specified timeframes.
                 Initial notification within 24 hours, intermediate report within 72 hours.</p>
            </div>
            <div>
              <h4 className="text-foreground font-medium mb-1">Third-Party Contracts</h4>
              <p>Contracts with ICT service providers must include specific provisions on security,
                 audit rights, exit strategies, and subcontracting oversight.</p>
            </div>
          </div>
        </div>

        <div className="bg-surface border border-border rounded-lg p-6">
          <h2 className="text-xl font-semibold text-foreground mb-4">Regulatory Context</h2>
          <div className="text-foreground-secondary text-sm space-y-2">
            <p>
              <span className="text-accent font-medium">European Supervisory Authorities (ESAs):</span>{' '}
              EBA, EIOPA, and ESMA are developing Regulatory Technical Standards (RTS) and
              Implementing Technical Standards (ITS) to provide detailed requirements.
            </p>
            <p>
              <span className="text-accent font-medium">Critical ICT TPP Oversight:</span>{' '}
              A new framework designates certain ICT third-party providers as &quot;critical&quot;
              and subjects them to direct EU-level oversight.
            </p>
            <p>
              <span className="text-accent font-medium">Proportionality:</span>{' '}
              Requirements are scaled based on entity size, nature, and risk profile.
              Simplified requirements apply to smaller entities.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <button
            onClick={exportToMarkdown}
            className="px-6 py-3 bg-cream/10 hover:bg-cream/20 text-foreground rounded-lg transition-colors"
          >
            Export Report
          </button>
          {commandCenterEnabled && (
            <button
              onClick={handleSaveToCommandCenter}
              disabled={saving || saved}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : saved ? 'Saved to Command Center' : 'Save to Command Center'}
            </button>
          )}
          <button
            onClick={() => {
              setView('landing');
              setCurrentSection(0);
              setAnswers({});
              setSaved(false);
            }}
            className="px-6 py-3 border border-border rounded-lg text-foreground hover:border-accent transition-colors"
          >
            Start Over
          </button>
        </div>
      </div>
    </div>
  );
}
